import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ROUTES } from './routes';
import { AuthGuard } from './AuthGuard';
import { RequireAdminConsoleSection } from './AccessGuard';
import { RequireAccessibleApplication } from './ApplicationGuard';
import { AppShell } from '@/layout/AppShell';
import { Spinner } from '@/components/ui/Spinner';
import { LandingRoute } from './LandingRoute';

const AuthCallbackPage = lazy(() => import('@/pages/auth/CallbackPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const LoggedOutPage = lazy(() => import('@/pages/auth/LoggedOutPage'));
const RegisterAccountPage = lazy(() => import('@/pages/auth/RegisterAccountPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const GestionAccessWorkspacePage = lazy(() => import('@/pages/gestion-access/GestionAccessWorkspacePage'));
const IntegrationWorkspacePage = lazy(() => import('@/pages/integration/IntegrationWorkspacePage'));
const ReferentialWorkspacePage = lazy(() => import('@/pages/referential/ReferentialWorkspacePage'));
const WorkspaceHostPage = lazy(() => import('@/pages/workspaces/WorkspaceHostPage'));
const UsersListPage = lazy(() => import('@/pages/users/UsersListPage'));
const RegisterIdentityPage = lazy(() => import('@/pages/users/RegisterIdentityPage'));
const UserDetailPage = lazy(() => import('@/pages/users/UserDetailPage'));
const OrganizationsListPage = lazy(() => import('@/pages/organizations/OrganizationsListPage'));
const CreateOrganizationPage = lazy(() => import('@/pages/organizations/CreateOrganizationPage'));
const OrganizationDetailPage = lazy(() => import('@/pages/organizations/OrganizationDetailPage'));
const AuthorizationPage = lazy(() => import('@/pages/authorization/AuthorizationPage'));
const ApplicationsPage = lazy(() => import('@/pages/applications/ApplicationsPage'));
const AuditPage = lazy(() => import('@/pages/audit/AuditPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const PreferencesPage = lazy(() => import('@/pages/settings/PreferencesPage'));
const UnauthorizedPage = lazy(() => import('@/pages/errors/UnauthorizedPage'));
const ForbiddenPage = lazy(() => import('@/pages/errors/ForbiddenPage'));
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'));

function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner size="md" />
    </div>
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path={ROUTES.AUTH_LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.AUTH_LOGGED_OUT} element={<LoggedOutPage />} />
        <Route path={ROUTES.AUTH_REGISTER} element={<RegisterAccountPage />} />
        <Route path={ROUTES.AUTH_CALLBACK} element={<AuthCallbackPage />} />
        <Route path={`${ROUTES.AUTH_CALLBACK}/*`} element={<AuthCallbackPage />} />

        <Route path={ROUTES.UNAUTHORIZED} element={<UnauthorizedPage />} />
        <Route path={ROUTES.FORBIDDEN} element={<ForbiddenPage />} />
        <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />

        <Route
          path="/*"
          element={
            <AuthGuard>
              <AppShell>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route index element={<LandingRoute />} />
                    <Route path="dashboard" element={<RequireAdminConsoleSection section="dashboard"><DashboardPage /></RequireAdminConsoleSection>} />
                    <Route path="gestion-access/*" element={<RequireAccessibleApplication clientId="gestion-access"><GestionAccessWorkspacePage /></RequireAccessibleApplication>} />
                    <Route path="integration/*" element={<RequireAccessibleApplication clientId="oss-integration"><IntegrationWorkspacePage /></RequireAccessibleApplication>} />
                    <Route path="declaration-sommaire/*" element={<RequireAccessibleApplication clientId="declaration-sommaire"><WorkspaceHostPage clientId="declaration-sommaire" /></RequireAccessibleApplication>} />
                    <Route path="maritime/*" element={<RequireAccessibleApplication clientId="oss-maritime-operations"><WorkspaceHostPage clientId="oss-maritime-operations" /></RequireAccessibleApplication>} />
                    <Route path="ens/*" element={<RequireAccessibleApplication clientId="oss-ens"><WorkspaceHostPage clientId="oss-ens" /></RequireAccessibleApplication>} />
                    <Route path="referential/*" element={<RequireAccessibleApplication clientId="oss-referential-service"><ReferentialWorkspacePage /></RequireAccessibleApplication>} />
                    <Route path="users" element={<RequireAdminConsoleSection section="users"><UsersListPage /></RequireAdminConsoleSection>} />
                    <Route path="users/new" element={<RequireAdminConsoleSection section="users"><RegisterIdentityPage /></RequireAdminConsoleSection>} />
                    <Route path="users/:id" element={<RequireAdminConsoleSection section="users"><UserDetailPage /></RequireAdminConsoleSection>} />
                    <Route path="organizations" element={<RequireAdminConsoleSection section="organizations"><OrganizationsListPage /></RequireAdminConsoleSection>} />
                    <Route path="organizations/new" element={<RequireAdminConsoleSection section="organizations"><CreateOrganizationPage /></RequireAdminConsoleSection>} />
                    <Route path="organizations/:id" element={<RequireAdminConsoleSection section="organizations"><OrganizationDetailPage /></RequireAdminConsoleSection>} />
                    <Route path="authorization" element={<RequireAdminConsoleSection section="authorization"><AuthorizationPage /></RequireAdminConsoleSection>} />
                    <Route path="applications" element={<RequireAdminConsoleSection section="applications"><ApplicationsPage /></RequireAdminConsoleSection>} />
                    <Route path="audit" element={<RequireAdminConsoleSection section="audit"><AuditPage /></RequireAdminConsoleSection>} />
                    <Route path="profile" element={<RequireAdminConsoleSection section="profile"><ProfilePage /></RequireAdminConsoleSection>} />
                    <Route path="settings/preferences" element={<PreferencesPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </AppShell>
            </AuthGuard>
          }
        />
      </Routes>
    </Suspense>
  );
}
